import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setTokenSource } from './api';
import { pendingLoginToken, redeemLoginToken } from './email-login';

// Supabase is used for auth only (F18 FR1). The API reads org_id and the MFA time from the session token.

export type Me = { user_id: string; roles: string[]; org: { id: string; name: string; primary_domain: string; plan: string; status: string; sandbox_only: boolean; sandbox_of?: string | null; new_experience?: boolean; ads_simulated?: boolean; approvals_required?: boolean;
  /** agency mode: this workspace is an agency, or (agency_org_id) a client workspace an agency manages, and who pays for it */
  is_agency?: boolean; agency_org_id?: string | null; agency_name?: string | null; billed_to?: 'agency' | 'client' | null } };
export type StaffMe = { user_id: string; email?: string; role: string; permissions: string[] };
type Ctx = {
  supabase: SupabaseClient | null; session: Session | null; me: Me | null; staff: StaffMe | null; loading: boolean;
  mfaAgeMs: number | null; refresh: () => Promise<void>; signOut: () => Promise<void>;
};
const AuthCtx = createContext<Ctx | null>(null);

/** When the session last passed a second factor (from the token's amr claim), as the API computes it. */
export function mfaVerifiedAt(session: Session | null): Date | null {
  if (!session) return null;
  try {
    const claims = JSON.parse(atob(session.access_token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    const amr = (claims.amr ?? []) as Array<{ method: string; timestamp: number }>;
    const mfa = amr.filter((a) => ['totp', 'phone', 'webauthn', 'mfa/totp'].includes(a.method)).map((a) => a.timestamp);
    return mfa.length ? new Date(Math.max(...mfa) * 1000) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children, client }: { children: ReactNode; client?: SupabaseClient | null }) {
  const [supabase, setSupabase] = useState<SupabaseClient | null>(client ?? null);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [staff, setStaff] = useState<StaffMe | null>(null);
  const [loading, setLoading] = useState(true);
  // the config, then the stored session, must be known before anything decides what to show
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (client !== undefined) return;
    api<{ supabase_url: string | null; supabase_publishable_key: string | null }>('/public/portal-config')
      .then((c) => { if (c.supabase_url && c.supabase_publishable_key) setSupabase(createClient(c.supabase_url, c.supabase_publishable_key)); else { setReady(true); setLoading(false); } })
      .catch(() => { setReady(true); setLoading(false); });
  }, [client]);

  useEffect(() => {
    if (!supabase) return;
    setTokenSource(async () => (await supabase.auth.getSession()).data.session?.access_token ?? null);
    supabase.auth.getSession().then(async ({ data }) => {
      // an emailed link's login token signs in only someone not signed in yet (src/lib/email-login.ts)
      const token = pendingLoginToken();
      const s = data.session ?? (token ? await redeemLoginToken(supabase, token) : null);
      setSession(s); setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, [supabase]);

  const refresh = useCallback(async () => {
    if (!ready) return;
    if (!session) { setMe(null); setStaff(null); setLoading(false); return; }
    setLoading(true);
    // /ops/me only for staff (it answers everyone else with a 404), or when /portal/me can't say (no workspace) or on /ops
    const m = await api<Me>('/portal/me').then((value) => ({ status: 'fulfilled' as const, value }), (reason: unknown) => ({ status: 'rejected' as const, reason }));
    const askStaff = m.status === 'rejected' || (m.value as Me & { staff?: boolean }).staff !== false || window.location.pathname.startsWith('/ops');
    const s = askStaff ? await api<StaffMe>('/ops/me').then((value) => ({ status: 'fulfilled' as const, value }), (reason: unknown) => ({ status: 'rejected' as const, reason })) : { status: 'rejected' as const, reason: null };
    // the workspace this session names was closed (by another owner, or by Outlevo): a fresh token names the next one
    // the person belongs to, or none (then the app offers to create one); the session state change reloads this
    if (m.status === 'rejected' && (m.reason as { code?: string })?.code === 'WORKSPACE_CLOSED' && supabase) {
      setMe(null); setStaff(s.status === 'fulfilled' ? s.value : null);
      await supabase.auth.refreshSession().catch(() => undefined);
      setLoading(false);
      return;
    }
    setMe(m.status === 'fulfilled' ? m.value : null);
    setStaff(s.status === 'fulfilled' ? s.value : null);
    setLoading(false);
  }, [session, ready, supabase]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<Ctx>(() => {
    const at = mfaVerifiedAt(session);
    return {
      supabase, session, me, staff, loading, mfaAgeMs: at ? Date.now() - at.getTime() : null, refresh,
      signOut: async () => { await supabase?.auth.signOut(); setMe(null); setStaff(null); },
    };
  }, [supabase, session, me, staff, loading, refresh]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('AuthProvider missing');
  return c;
}

/** The session when there is one (components that also render outside the app, e.g. in tests). */
export const useAuthOptional = () => useContext(AuthCtx);

export const MFA_WINDOW_MS = 12 * 3_600_000;
/** F18 approval rule 2: the Approve button needs a second factor within the last 12 hours. */
export const mfaFresh = (ageMs: number | null) => ageMs !== null && ageMs <= MFA_WINDOW_MS;
export const hasRole = (me: Me | null, ...roles: string[]) => Boolean(me?.roles.some((r) => roles.includes(r)));
